import React, { useContext, Fragment, useEffect, useState } from 'react';

import { CartContext } from '../context/CartContext';
import { buildQuotePayload } from '../utils/orderPayload';

import Button from './ui/Button';

const API_URL = process.env.REACT_APP_API_URL;

const StepperButton = ({ onClick, label, children }) => (
  <button
    onClick={onClick}
    aria-label={label}
    className="flex h-8 w-8 items-center justify-center rounded-full border border-cocoa-200 bg-white text-cocoa-700 transition-colors hover:bg-cocoa-50"
  >
    {children}
  </button>
);

const Cart = ({ onCheckout }) => {
  const {
    cart,
    removeFromCart,
    updateQuantity,
    getSubtotal,
    isCartOpen,
    setIsCartOpen,
  } = useContext(CartContext);
  const [minimumOrder, setMinimumOrder] = useState(null);

  // A regra de pedido mínimo vem do backend (quote), não é calculada aqui.
  useEffect(() => {
    if (!isCartOpen || cart.length === 0) {
      setMinimumOrder(null);
      return undefined;
    }

    let active = true;
    const timer = setTimeout(() => {
      fetch(`${API_URL}/api/orders/quote/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(
          buildQuotePayload(cart, { shippingType: 'PICKUP', zipCode: '' })
        ),
      })
        .then((response) => (response.ok ? response.json() : Promise.reject()))
        .then((data) => {
          if (!active) return;
          setMinimumOrder({
            total: Number(data.minimum_order_total),
            met: data.minimum_order_met,
            remaining: Number(data.minimum_order_remaining),
          });
        })
        .catch(() => {
          if (active) setMinimumOrder(null);
        });
    }, 200);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [isCartOpen, cart]);

  if (!isCartOpen) return null;

  const minimumNotMet = Boolean(minimumOrder && !minimumOrder.met);

  return (
    <Fragment>
      <div
        className="fixed inset-0 z-40 animate-fade-in bg-cocoa-950/40 backdrop-blur-[2px]"
        onClick={() => setIsCartOpen(false)}
        aria-hidden="true"
      />

      <aside
        className="fixed right-0 top-0 z-50 flex h-full w-full max-w-md animate-slide-in-right flex-col bg-white shadow-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Carrinho"
      >
        <div className="flex items-center justify-between border-b border-cocoa-100 bg-cocoa-800 px-6 py-4 text-cream-50">
          <h2 className="font-display text-xl font-semibold">Carrinho</h2>
          <button
            onClick={() => setIsCartOpen(false)}
            aria-label="Fechar carrinho"
            className="rounded-full p-2 text-cream-50/90 transition-colors hover:bg-white/15 hover:text-white"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-5">
          {cart.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center text-center text-ink-muted">
              <svg className="mb-4 h-20 w-20 text-cocoa-200" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.5 2.76-6.673M6 5.272L7.5 9.75m0 0l3.052 7.63m-3.052-7.63l6.105-2.67" />
              </svg>
              <p className="text-base font-medium">Seu carrinho está vazio</p>
              <p className="mt-1 text-sm">Adicione um produto para começar.</p>
            </div>
          ) : (
            <ul className="space-y-3">
              {cart.map((item) => (
                <li key={item.id} className="rounded-card border border-cocoa-100 bg-cream-50 p-3 shadow-card">
                  <div className="flex gap-3">
                    <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white p-1">
                      {item.customImage ? (
                        <img src={item.customImage} alt="Personalização" className="h-full w-full object-contain" />
                      ) : item.product.image ? (
                        <img src={item.product.image} alt={item.product.name} className="h-full w-full object-cover" />
                      ) : (
                        <svg className="h-6 w-6 text-cocoa-300" fill="none" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-2.29 0-4.2 1.69-4.594 4.5H3.375c-1.45 0-2.25.828-2.25 2.25V15c0 1.45.8 2.25 2.25 2.25h3.375v-5.25c0-2.485 2.099-4.5 4.688-4.5 2.29 0 4.2 1.69 4.594 4.5H3.375c-1.45 0-2.25.828-2.25 2.25V15c0 1.45.8 2.25 2.25 2.25h3.375v-5.25c0-2.485 2.099-4.5 4.688-4.5" /></svg>
                      )}
                    </div>

                    <div className="min-w-0 flex-1">
                      <h3 className="truncate text-sm font-semibold text-ink">{item.product.name}</h3>
                      {item.sizeLabel && item.product.sizes ? (
                        <p className="text-xs text-ink-muted">
                          Tamanho:{' '}
                          <span className="inline-flex items-center rounded-sm bg-cocoa-200 px-1.5 py-0.5 text-xs font-medium text-cocoa-800">
                            {item.sizeLabel} — R$ {Number(item.product.sizes.find(s => s.label === item.sizeLabel)?.price || 0).toFixed(2)}
                          </span>
                        </p>
                      ) : (
                        <p className="text-xs text-ink-muted">{item.product.type}</p>
                      )}
                    </div>

                    <button
                      onClick={() => removeFromCart(item.id)}
                      aria-label={`Remover ${item.product.name}`}
                      className="shrink-0 rounded-full p-1.5 text-ink-muted transition-colors hover:bg-clay-50 hover:text-clay-600"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 7h12M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2m1 0v12a1 1 0 01-1 1H8a1 1 0 01-1-1V7m3 4v5m4-5v5" />
                      </svg>
                    </button>
                  </div>

                  <div className="mt-3 flex items-center gap-2">
                    <StepperButton onClick={() => updateQuantity(item.id, item.quantity - 1)} label="Diminuir quantidade">
                      <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" d="M5 12h14" /></svg>
                    </StepperButton>
                    <span className="w-10 text-center text-sm font-semibold text-ink">{item.quantity}</span>
                    <StepperButton onClick={() => updateQuantity(item.id, item.quantity + 1)} label="Aumentar quantidade">
                      <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" d="M12 5v14M5 12h14" /></svg>
                    </StepperButton>
                    <span className="ml-auto text-sm font-bold text-cocoa-800">
                      {(() => {
                        let unitPrice = item.product.price || 0;
                        if (item.sizeLabel && item.product.sizes) {
                          const found = item.product.sizes.find(s => s.label === item.sizeLabel);
                          if (found) unitPrice = parseFloat(found.price);
                        }
                        return `R$ ${(unitPrice * item.quantity).toFixed(2)}`;
                      })()}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {cart.length > 0 && (
          <div className="border-t border-cocoa-100 bg-cream-50 px-5 py-4">
            <div className="mb-1 flex items-center justify-between">
              <span className="text-sm font-semibold text-ink-soft">Subtotal (estimado)</span>
              <span className="font-display text-2xl font-semibold text-cocoa-800">
                R$ {getSubtotal().toFixed(2)}
              </span>
            </div>
            <p className="mb-4 text-xs text-ink-muted">
              Descontos por quantidade e frete são calculados no checkout.
            </p>
            {minimumNotMet && (
              <p
                role="alert"
                className="mb-3 rounded-xl border border-honey-200 bg-honey-50 px-3 py-2 text-xs font-medium text-honey-700"
              >
                Pedido mínimo de R$ {minimumOrder.total.toFixed(2)}. Faltam R${' '}
                {minimumOrder.remaining.toFixed(2)} em produtos para finalizar.
              </p>
            )}
            <Button
              className="w-full"
              disabled={minimumNotMet}
              onClick={() => {
                setIsCartOpen(false);
                onCheckout();
              }}
            >
              Finalizar Pedido
            </Button>
          </div>
        )}
      </aside>
    </Fragment>
  );
};

export default Cart;
